import React from 'react';
import { useGamification } from '../hooks/useGamification';

interface GamificationGateProps {
  /** Content to render when gamification is enabled */
  children: React.ReactNode;
  /** Content to render when gamification is disabled */
  fallback?: React.ReactNode;
  /** Whether to show loading state while checking the flag */
  showLoading?: boolean;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Whether to invert the check (render children when gamification is disabled) */
  invert?: boolean;
  /** Specific gamification feature to check (defaults to general gamification) */
  feature?: 'coins' | 'badges' | 'leaderboard' | 'progress' | 'rewards';
}

/**
 * Component that conditionally renders children based on gamification feature flag state
 * This is a specialized version of FeatureGate specifically for gamification features
 * 
 * @example
 * ```tsx
 * <GamificationGate>
 *   <CoinBalance />
 * </GamificationGate>
 * 
 * <GamificationGate feature="badges">
 *   <BadgeDisplay />
 * </GamificationGate>
 * 
 * <GamificationGate fallback={<div>Gamification disabled</div>}>
 *   <LeaderboardComponent />
 * </GamificationGate>
 * ```
 */
export function GamificationGate({
  children,
  fallback = null,
  showLoading: _showLoading = false,
  loadingComponent: _loadingComponent = <div>Loading...</div>,
  invert = false,
  feature
}: GamificationGateProps): JSX.Element | null {
  const {
    isGamificationEnabled,
    shouldShowCoins,
    shouldShowBadges,
    shouldShowLeaderboard,
    shouldShowProgress,
    shouldShowRewards
  } = useGamification();

  // Determine which feature to check
  let shouldRender = isGamificationEnabled;
  
  if (feature) {
    switch (feature) {
      case 'coins':
        shouldRender = shouldShowCoins;
        break;
      case 'badges':
        shouldRender = shouldShowBadges;
        break;
      case 'leaderboard':
        shouldRender = shouldShowLeaderboard;
        break;
      case 'progress':
        shouldRender = shouldShowProgress;
        break;
      case 'rewards':
        shouldRender = shouldShowRewards;
        break;
      default:
        shouldRender = isGamificationEnabled;
    }
  }

  // Apply invert logic
  if (invert) {
    shouldRender = !shouldRender;
  }

  if (shouldRender) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

/**
 * Higher-order component version of GamificationGate for wrapping components
 */
export function withGamification<P extends object>(
  Component: React.ComponentType<P>,
  feature?: 'coins' | 'badges' | 'leaderboard' | 'progress' | 'rewards',
  fallback?: React.ReactNode
) {
  const WrappedComponent = (props: P) => (
    <GamificationGate feature={feature} fallback={fallback}>
      <Component {...props} />
    </GamificationGate>
  );

  WrappedComponent.displayName = `withGamification(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

/**
 * Component for showing content when gamification is NOT available
 */
export function GamificationNotAvailable({ 
  title: _title = "Gamification Not Available",
  message = "Gamification features are currently disabled.",
  className = ""
}: {
  title?: string;
  message?: string;
  className?: string;
}) {
  return (
    <div className={`text-center py-4 px-4 ${className}`}>
      <div className="max-w-md mx-auto">
        <p className="text-gray-600 text-sm">{message}</p>
      </div>
    </div>
  );
}

/**
 * Hook for conditionally executing effects based on gamification state
 */
export function useGamificationEffect(
  effect: React.EffectCallback,
  deps?: React.DependencyList,
  feature?: 'coins' | 'badges' | 'leaderboard' | 'progress' | 'rewards'
) {
  const {
    isGamificationEnabled,
    shouldShowCoins,
    shouldShowBadges,
    shouldShowLeaderboard,
    shouldShowProgress,
    shouldShowRewards
  } = useGamification();
  
  React.useEffect(() => {
    let shouldExecute = isGamificationEnabled;
    
    if (feature) {
      switch (feature) {
        case 'coins':
          shouldExecute = shouldShowCoins;
          break;
        case 'badges':
          shouldExecute = shouldShowBadges;
          break;
        case 'leaderboard':
          shouldExecute = shouldShowLeaderboard;
          break;
        case 'progress':
          shouldExecute = shouldShowProgress;
          break;
        case 'rewards':
          shouldExecute = shouldShowRewards;
          break;
      }
    }
    
    if (shouldExecute) {
      return effect();
    }
  }, [isGamificationEnabled, shouldShowCoins, shouldShowBadges, shouldShowLeaderboard, shouldShowProgress, shouldShowRewards, ...(deps || [])]);
}